import { toast } from 'react-toastify';
import * as types from './types';
import * as posMenuService from '../../services/posMenuService';
import { scopeExternalIdsToTargetMenu } from 'utils/posMenuImport';
// Loading and error actions
export const setLoading = (loading) => ({
  type: types.POS_MENU_LOADING,
  payload: loading,
});

export const setError = (error) => ({
  type: types.POS_MENU_ERROR,
  payload: error,
});

// Fetch menu ID by business
export const fetchMenuId = (businessId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const externalMenuId = await posMenuService.getMenuIdByBusiness(businessId);
    if (externalMenuId) {
      dispatch({ type: types.POS_MENU_SET_MENU_ID, payload: externalMenuId });
      return externalMenuId;
    } else {
      throw new Error('No menu found for this business');
    }
  } catch (error) {
    dispatch(setError(error.message || 'Failed to fetch menu ID'));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Fetch menu map
export const fetchMenuMap =
  (externalMenuId, forceFresh = false) =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const data = await posMenuService.getMenuMap(externalMenuId, forceFresh);
      dispatch({
        type: types.POS_MENU_SET_MAP,
        payload: data,
      });
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      dispatch(setError(error.message));
      dispatch(setLoading(false));
      throw error;
    }
  };

// Returns the menu map in the shape accepted by importMenuFromJson
export const exportPosMenu = (businessId) => async () => {
  const externalMenuId = await posMenuService.getMenuIdByBusiness(businessId);
  if (!externalMenuId) {
    throw new Error('No menu found for this business');
  }
  return posMenuService.getMenuMap(externalMenuId, true);
};

// Menu import actions
export const importMenuFromJson =
  (businessUuid, businessId, menu) => async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let targetMap;
      try {
        const targetMenuId =
          await posMenuService.getMenuIdByBusiness(businessId);
        if (!targetMenuId) {
          throw new Error('No menu found for this business');
        }
        targetMap = await posMenuService.getMenuMap(targetMenuId, true);
      } catch (error) {
        toast.error('Failed to load the current menu. Import was not started.');
        throw error;
      }
      let scopedMenu;
      try {
        scopedMenu = scopeExternalIdsToTargetMenu(menu, targetMap);
      } catch (error) {
        toast.error(error.message);
        throw error;
      }
      const data = await posMenuService.importMenuMap(businessUuid, scopedMenu);
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      dispatch(setError(error.message));
      dispatch(setLoading(false));
      throw error;
    }
  };

export const emptyPosMenu = (businessUuid) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const data = await posMenuService.emptyMenu(businessUuid);
    dispatch(setLoading(false));
    return data;
  } catch (error) {
    dispatch(setError(error.message));
    dispatch(setLoading(false));
    throw error;
  }
};

// Category actions
export const fetchCategories = (menuId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const data = await posMenuService.getCategories(menuId);
    dispatch({
      type: types.POS_MENU_SET_CATEGORIES,
      payload: data,
    });
    dispatch(setLoading(false));
    return data;
  } catch (error) {
    dispatch(setError(error.message));
    dispatch(setLoading(false));
    throw error;
  }
};

export const createCategory = (categoryData) => async (dispatch) => {
  try {
    const data = await posMenuService.createCategory(categoryData);
    // Ensure all fields from request are preserved in case backend doesn't return them
    const categoryWithAllFields = {
      ...data,
      itemIds: data.itemIds || [], // Initialize empty itemIds array
    };
    dispatch({
      type: types.POS_MENU_ADD_CATEGORY,
      payload: categoryWithAllFields,
    });
    return categoryWithAllFields;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const updateCategory =
  (categoryId, categoryData) => async (dispatch, getState) => {
    try {
      const data = await posMenuService.updateCategory(
        categoryId,
        categoryData
      );
      // Read after the await so edits that landed meanwhile aren't reverted
      const existingCategory =
        getState().posMenu.menuMap?.categories?.[categoryId];
      // Preserve itemIds if explicitly provided in request (for sorting),
      // otherwise fall back to API response or existing state
      const categoryWithPreservedFields = {
        ...existingCategory,
        ...data,
        itemIds:
          categoryData.itemIds ||
          data.itemIds ||
          existingCategory?.itemIds ||
          [],
      };
      dispatch({
        type: types.POS_MENU_UPDATE_CATEGORY,
        payload: categoryWithPreservedFields,
      });
      return categoryWithPreservedFields;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const deleteCategory = (categoryId) => async (dispatch) => {
  try {
    await posMenuService.deleteCategory(categoryId);
    dispatch({
      type: types.POS_MENU_DELETE_CATEGORY,
      payload: categoryId,
    });
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const reorderCategories =
  (orderedCategoryIds) => async (dispatch, getState) => {
    try {
      const { menuMap } = getState().posMenu;

      // Update sort field for each category based on new order
      const updates = orderedCategoryIds.map((categoryId, index) => {
        const existingCategory = menuMap?.categories?.[categoryId];
        if (existingCategory) {
          return dispatch(updateCategory(categoryId, { sort: index }));
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

// Item actions
export const createItem = (itemData) => async (dispatch) => {
  try {
    const data = await posMenuService.createItem(itemData);
    // Backend doesn't return category field, so preserve it from request
    const itemWithCategory = {
      ...data,
      category: data.category || itemData.category,
    };
    dispatch({
      type: types.POS_MENU_ADD_ITEM,
      payload: itemWithCategory,
    });
    return itemWithCategory;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const updateItem = (itemId, itemData) => async (dispatch, getState) => {
  try {
    const data = await posMenuService.updateItem(itemId, itemData);
    // Read after the await so edits that landed meanwhile aren't reverted
    const existingItem = getState().posMenu.menuMap?.items?.[itemId];
    // Preserve existing fields, then overlay API response, then overlay the data we sent
    // (in case API doesn't return certain fields we updated), then handle specific fields
    const itemWithPreservedFields = {
      ...existingItem,
      ...data,
      ...itemData, // Ensure the fields we sent are preserved
      // '' is an explicit "no category", so only fall back when category wasn't sent
      category: Object.prototype.hasOwnProperty.call(itemData, 'category')
        ? itemData.category
        : data.category || existingItem?.category,
      // Preserve title (internal name) - prefer itemData if provided, then API, then existing
      title: itemData.title || data.title || existingItem?.title,
      // Prefer itemData.modifierGroupIds if explicitly provided (for sorting),
      // otherwise fall back to API response or existing state
      modifierGroupIds:
        itemData.modifierGroupIds ||
        data.modifierGroupIds ||
        existingItem?.modifierGroupIds,
      // Prefer itemData.preselectOptions if explicitly provided,
      // otherwise fall back to API response or existing state
      preselectOptions:
        itemData.preselectOptions ||
        data.preselectOptions ||
        existingItem?.preselectOptions,
    };
    dispatch({
      type: types.POS_MENU_UPDATE_ITEM,
      payload: itemWithPreservedFields,
    });
    return itemWithPreservedFields;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteItem = (itemId) => async (dispatch) => {
  try {
    await posMenuService.deleteItem(itemId);
    dispatch({
      type: types.POS_MENU_DELETE_ITEM,
      payload: itemId,
    });
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Creates the initial inventory record for a POS menu item.
// Endpoint: POST /cloud-pos/menu/item/{itemId}/inventory
export const generatePosItemInventory =
  (itemId, businessUuid, overrides = {}) => async (dispatch) => {
    try {
      const payload = {
        businessUuid,
        unit: 'each',
        stock: 1,
        minStockLevel: 10,
        maxStockLevel: 1000,
        unitPrice: null,
        trackInventory: true,
        ...overrides,
      };
      const data = await posMenuService.generateItemInventory(itemId, payload);
      return data;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const updateItemSoldOut =
  (itemId, soldOutData) => async (dispatch, getState) => {
    try {
      const data = await posMenuService.updateItemSoldOut(itemId, soldOutData);
      // Update the item in the store with the new soldOut status
      const existingItem = getState().posMenu.menuMap?.items?.[itemId];
      if (existingItem) {
        dispatch({
          type: types.POS_MENU_UPDATE_ITEM,
          payload: {
            ...existingItem,
            soldOut: soldOutData,
          },
        });
      }
      return data;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

// Modifier Group actions
export const fetchIngredients = (menuId) => async (dispatch) => {
  try {
    const data = await posMenuService.getIngredients(menuId);
    dispatch({
      type: types.POS_MENU_SET_INGREDIENTS,
      payload: data,
    });
    return data;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const createModifierGroup = (modifierGroupData) => async (dispatch) => {
  try {
    const data = await posMenuService.createModifierGroup(modifierGroupData);
    // Ensure optionIds is initialized as empty array for new modifier groups
    const modifierGroupWithOptions = {
      ...data,
      optionIds: data.optionIds || [],
    };
    dispatch({
      type: types.POS_MENU_ADD_MODIFIER_GROUP,
      payload: modifierGroupWithOptions,
    });
    return modifierGroupWithOptions;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const updateModifierGroup =
  (modifierGroupId, modifierGroupData) => async (dispatch, getState) => {
    try {
      const data = await posMenuService.updateModifierGroup(
        modifierGroupId,
        modifierGroupData
      );
      // Read after the await so edits that landed meanwhile aren't reverted
      const existingMg =
        getState().posMenu.menuMap?.modifierGroups?.[modifierGroupId];
      // Preserve existing fields, then overlay API response, then overlay the data we sent
      // (in case API doesn't return certain fields we updated)
      const modifierGroupWithPreservedFields = {
        ...existingMg,
        ...data,
        ...modifierGroupData, // Ensure the fields we sent are preserved
        // Preserve optionIds and options arrays from local state to avoid overwriting
        // with stale backend data (e.g., after adding a new option locally)
        optionIds:
          modifierGroupData.optionIds ||
          existingMg?.optionIds ||
          data.optionIds ||
          [],
        options:
          modifierGroupData.options ||
          existingMg?.options ||
          data.options ||
          [],
        preselectOptions:
          modifierGroupData.preselectOptions ||
          data.preselectOptions ||
          existingMg?.preselectOptions,
      };
      dispatch({
        type: types.POS_MENU_UPDATE_MODIFIER_GROUP,
        payload: modifierGroupWithPreservedFields,
      });
      return modifierGroupWithPreservedFields;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const deleteModifierGroup = (modifierGroupId) => async (dispatch) => {
  try {
    await posMenuService.deleteModifierGroup(modifierGroupId);
    dispatch({
      type: types.POS_MENU_DELETE_MODIFIER_GROUP,
      payload: modifierGroupId,
    });
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Ingredient actions
export const createIngredient = (ingredientData) => async (dispatch) => {
  try {
    const data = await posMenuService.createIngredient(ingredientData);
    dispatch({
      type: types.POS_MENU_ADD_INGREDIENT,
      payload: data,
    });
    return data;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const updateIngredient =
  (ingredientId, ingredientData) => async (dispatch) => {
    try {
      const data = await posMenuService.updateIngredient(
        ingredientId,
        ingredientData
      );
      dispatch({
        type: types.POS_MENU_UPDATE_INGREDIENT,
        payload: data,
      });
      return data;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const deleteIngredient = (ingredientId) => async (dispatch) => {
  try {
    await posMenuService.deleteIngredient(ingredientId);
    dispatch({
      type: types.POS_MENU_DELETE_INGREDIENT,
      payload: ingredientId,
    });
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Relationship actions
export const createCategoryItem = (relationshipData) => async (dispatch) => {
  try {
    await posMenuService.createCategoryItem(relationshipData);
    // Optionally refresh menu map
    return true;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteCategoryItem = (relationshipData) => async (dispatch) => {
  try {
    await posMenuService.deleteCategoryItem(relationshipData);
    // Optionally refresh menu map
    return true;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const createItemModifierGroup =
  (relationshipData) => async (dispatch) => {
    try {
      await posMenuService.createItemModifierGroup(relationshipData);
      dispatch({
        type: types.POS_MENU_ATTACH_MODIFIER_GROUP,
        payload: {
          itemId: relationshipData.itemId,
          modifierGroupId: relationshipData.modifierGroupId,
        },
      });
      return true;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const deleteItemModifierGroup =
  (relationshipData) => async (dispatch) => {
    try {
      await posMenuService.deleteItemModifierGroup(relationshipData);
      dispatch({
        type: types.POS_MENU_DETACH_MODIFIER_GROUP,
        payload: {
          itemId: relationshipData.itemId,
          modifierGroupId: relationshipData.modifierGroupId,
        },
      });
      return true;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const createItemIngredient = (relationshipData) => async (dispatch) => {
  try {
    await posMenuService.createItemIngredient(relationshipData);
    return true;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteItemIngredient = (relationshipData) => async (dispatch) => {
  try {
    await posMenuService.deleteItemIngredient(relationshipData);
    return true;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const createModifierGroupOption =
  (relationshipData) => async (dispatch) => {
    try {
      await posMenuService.createModifierGroupOption(relationshipData);
      dispatch({
        type: types.POS_MENU_ADD_MODIFIER_GROUP_OPTION,
        payload: {
          modifierGroupId: relationshipData.modifierGroupId,
          optionId: relationshipData.optionId,
        },
      });
      return true;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const deleteModifierGroupOption =
  (relationshipData) => async (dispatch) => {
    try {
      await posMenuService.deleteModifierGroupOption(relationshipData);
      dispatch({
        type: types.POS_MENU_REMOVE_MODIFIER_GROUP_OPTION,
        payload: {
          modifierGroupId: relationshipData.modifierGroupId,
          optionId: relationshipData.optionId,
        },
      });
      return true;
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

export const reorderModifierGroupOptions = (modifierGroupId, optionIds) => ({
  type: types.POS_MENU_REORDER_MODIFIER_GROUP_OPTIONS,
  payload: { modifierGroupId, optionIds },
});

export const bulkCreateModifierGroupOptions =
  (modifierGroupId, options, menuId) => async (dispatch) => {
    try {
      const orderedOptionIds = options.map(({ optionId }) => optionId);

      await Promise.all(
        options.map(({ optionId, sort }) =>
          posMenuService.createModifierGroupOption({
            modifierGroupId,
            optionId,
            menuId,
            sort,
          })
        )
      );

      // Promise.all doesn't guarantee backend processing order, so the
      // modifier group's optionIds on the backend may be in the wrong order.
      // Explicitly set the correct order after all options are created.
      await posMenuService.updateModifierGroup(modifierGroupId, {
        optionIds: orderedOptionIds,
      });

      dispatch({
        type: types.POS_MENU_REORDER_MODIFIER_GROUP_OPTIONS,
        payload: {
          modifierGroupId,
          optionIds: orderedOptionIds,
        },
      });
    } catch (error) {
      dispatch(setError(error.message));
      throw error;
    }
  };

// UI state actions
export const setSelectedEntity = (entity, entityType, skipHistory = false) => ({
  type: types.POS_MENU_SET_SELECTED_ENTITY,
  payload: { entity, entityType, skipHistory },
});

export const clearSelected = () => ({
  type: types.POS_MENU_CLEAR_SELECTED,
});

export const goBack = () => ({
  type: types.POS_MENU_GO_BACK,
});

export const goToHistoryIndex = (index) => ({
  type: types.POS_MENU_GO_TO_HISTORY_INDEX,
  payload: index,
});
